import type React from 'react';

export * from './array.utils';
// Format utils - exclude formatPrice to avoid conflict with price.utils
export { formatNumberWithMask } from './format';
export * from './flash-sale-tab.utils';
export * from './coupon-image-utils';
export * from './image.utils';
export * from './price.utils';
export * from './helpers';
export * from './navigation.utils';

// React Native stubs - base primitives only (View, Pressable, TouchableOpacity, FlatList, etc.)
export {
    View,
    Pressable,
    TouchableOpacity,
    FlatList,
    RefreshControl,
    StyleSheet,
    Animated,
    SectionList,
} from './react-native-stubs';
export type {
    SectionListData,
    SectionListRenderItemInfo,
    SectionListRenderItem,
    SectionListProps,
} from './react-native-stubs';

// ============================================================================
// Theme exports - from UI Core
// ============================================================================
export {
    colorsPalettes,
    schemes,
    getScheme,
    lightScheme,
    darkScheme,
    defaultTheme as uiDefaultTheme,
} from '@kidslabs/mario-nextjs-react-ui-core';

// Color scheme hook for web
export type ColorScheme = 'light' | 'dark';

/**
 * useColorScheme hook for web
 * Returns the current color scheme based on system preference
 */
export const useColorScheme = (): ColorScheme => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// ============================================================================
// Mobile API Wrappers (onPress, source, style arrays, etc.)
// These provide mobile-style API over web components for backward compatibility
// ============================================================================
export {
    Button,
    Image,
    Text,
    Card,
    RadioButton,
    KidsLoading,
    CouponListSkeleton,
    CouponSection,
    TextIcon,
} from './mobile-wrappers';
export type {
    MobileButtonProps,
    MobileImageProps,
    MobileTextProps,
    MobileCardProps,
    MobileRadioButtonProps,
    MobileKidsLoadingProps,
    MobileCouponListSkeletonProps,
    MobileCouponSectionProps,
    TextIconProps,
} from './mobile-wrappers';

// ============================================================================
// UI Core re-exports (direct imports for components without mobile wrappers)
// ============================================================================
export {
    // Atoms
    Icon,
    Divider,
    Spacer,
    Badge,
    Chip,
    ProgressBar,
    Checkbox,
    ActivityIndicator,
    Avatar,
    HelperText,
    IconChevronRight,
    // Molecules
    Modal,
    Tabs,
    Input,
    Alert,
    Skeleton,
    Toast,
    // Organisms & Complex
    NoData,
    DotsLoader,
    CouponInformation,
    CountdownCard as CountDownCard,
    CountdownCard,
    QuantityStepper,
    MyCouponCard,
    VoucherCard,
    // HOCs
    withBottomSheets,
    // Mobile-compatible withAppBottomSheet HOC from UI Core
    withAppBottomSheet,
    MarioBottomSheetFlatList,
    // Theme
    defaultTheme,
    cn,
} from '@kidslabs/mario-nextjs-react-ui-core';
export type {
    WithBottomSheetsInjectedProps,
    AppBottomSheetInjectedProps as BottomSheetExtraProps,
    MarioBottomSheetFlatListProps,
} from '@kidslabs/mario-nextjs-react-ui-core';

// ============================================================================
// Module-specific shared components (CSS Modules + Nova theme)
// ============================================================================
export {
    PromotionalItemCard,
    CouponInput,
    CouponAppliedBadge,
    ProductProgressBar,
    BottomSheet,
    withDialog,
} from '../components/shared';
export type {
    PromotionalItemCardProps,
    CouponInputProps,
    CouponAppliedBadgeProps,
    ProductProgressBarProps,
    BottomSheetProps,
    WithDialogProps,
} from '../components/shared';

// ============================================================================
// Legacy compatibility - useTheme hook returning theme object
// ============================================================================

/**
 * Legacy Theme Types (for backward compatibility with mobile code patterns)
 * @deprecated Use CSS variables from theme/flash-sale.tokens.css instead
 */
export interface ThemeColors {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    error: string;
    text: string;
    onPrimary: string;
    onSecondary: string;
    onBackground: string;
    onSurface: string;
    onError: string;
    disabled: string;
    placeholder: string;
    backdrop: string;
    notification: string;
    outline: string;
    schemes: {
        light: {
            primary: string;
            secondary: string;
            background: string;
            surface: string;
            error: string;
            text: string;
            onPrimary: string;
            outlineVariant: string;
        };
        dark: {
            primary: string;
            secondary: string;
            background: string;
            surface: string;
            error: string;
            text: string;
            onPrimary: string;
            outlineVariant: string;
        };
    };
}

export interface ThemeComponents {
    button: {
        backgroundColor: string;
        textColor: string;
        borderRadius: number;
    };
    card: {
        backgroundColor: string;
        borderRadius: number;
    };
    input: {
        backgroundColor: string;
        borderColor: string;
        borderRadius: number;
    };
    countDownCard: {
        backgroundColor: string;
        textColor: string;
    };
    flashSaleTab: {
        activeColor: string;
        inactiveColor: string;
        countdown: {
            backgroundColor: string;
        };
    };
}

export interface ThemeSizes {
    icon: Record<string, number>;
    spacing: Record<string, number>;
    borderRadius: Record<string, number>;
}

export interface Theme {
    colors: ThemeColors;
    spacing: (multiplier: number) => number;
    roundness: number;
    fonts: Record<string, { fontFamily: string; fontWeight?: string }>;
    components: ThemeComponents;
    size: ThemeSizes;
}

const createLegacyTheme = (): Theme => ({
    colors: {
        primary: '#21409A',
        secondary: '#22C4F4',
        background: '#FFFFFF',
        surface: '#FFFFFF',
        error: '#ED1C24',
        text: '#1A1A1A',
        onPrimary: '#FFFFFF',
        onSecondary: '#000000',
        onBackground: '#1A1A1A',
        onSurface: '#1A1A1A',
        onError: '#FFFFFF',
        disabled: '#9E9E9E',
        placeholder: '#9E9E9E',
        backdrop: 'rgba(0, 0, 0, 0.5)',
        notification: '#FF9800',
        outline: '#E0E0E0',
        schemes: {
            light: {
                primary: '#21409A',
                secondary: '#22C4F4',
                background: '#FFFFFF',
                surface: '#FFFFFF',
                error: '#ED1C24',
                text: '#1A1A1A',
                onPrimary: '#FFFFFF',
                outlineVariant: '#E0E0E0',
            },
            dark: {
                primary: '#5772CE',
                secondary: '#22C4F4',
                background: '#121212',
                surface: '#1E1E1E',
                error: '#CF6679',
                text: '#FFFFFF',
                onPrimary: '#000000',
                outlineVariant: '#444444',
            },
        },
    },
    spacing: (multiplier: number) => multiplier * 8,
    roundness: 8,
    fonts: {
        regular: { fontFamily: 'Inter, system-ui, sans-serif' },
        medium: { fontFamily: 'Inter, system-ui, sans-serif', fontWeight: '500' },
        bold: { fontFamily: 'Inter, system-ui, sans-serif', fontWeight: '700' },
    },
    components: {
        button: { backgroundColor: '#21409A', textColor: '#FFFFFF', borderRadius: 8 },
        card: { backgroundColor: '#FFFFFF', borderRadius: 12 },
        input: { backgroundColor: '#F5F5F5', borderColor: '#E0E0E0', borderRadius: 8 },
        countDownCard: { backgroundColor: '#21409A', textColor: '#FFFFFF' },
        flashSaleTab: {
            activeColor: '#ED1C24',
            inactiveColor: '#666666',
            countdown: { backgroundColor: '#21409A' },
        },
    },
    size: {
        icon: { small: 16, medium: 24, large: 32 },
        spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 },
        borderRadius: { sm: 4, md: 8, lg: 12, xl: 16 },
    },
});

const legacyTheme = createLegacyTheme();

/**
 * Flash Sale Theme type for legacy compatibility
 * @deprecated Use CSS variables and UI Core theme instead
 */
export type FlashSaleTheme = Theme;

/**
 * Flash Sale theme instance for legacy compatibility
 * @deprecated Use CSS variables and UI Core theme instead
 */
export const flashSaleTheme = legacyTheme;

/**
 * Legacy useTheme hook for backward compatibility.
 * @deprecated Prefer using CSS variables and the theme/flash-sale.tokens.css
 */
export const useTheme = (): Theme => legacyTheme;

// ============================================================================
// Placeholder components for mobile-specific widgets
// ============================================================================

/**
 * GiftProductSelectorWidget placeholder
 * Mobile-specific widget not needed on web
 */
export interface GiftProductSelectorWidgetProps {
    product?: unknown;
    onSelect?: () => void;
}
export const GiftProductSelectorWidget: React.FC<GiftProductSelectorWidgetProps> = () => null;
